import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  DashboardListItemViewFragment,
  DashboardUseCase,
  useListDashboardsQuery,
} from 'generated/graphql';
import { useEffect } from 'react';
import { useFetchMore } from 'v2/core/repository/edge-server/utils/records';
import { useGetUser } from 'v2/core/services/user';

interface UseGetTemplatesOptions {
  onCompleted?: (templates: DashboardListItemViewFragment[]) => void;
  skip?: boolean;
  sortAsc?: boolean;
}

export const useGetTemplates = ({
  onCompleted = () => {},
  skip = false,
  sortAsc = true,
}: UseGetTemplatesOptions = {}) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const {
    data,
    error,
    loading,
    fetchMore: eFetchMore,
    refetch,
  } = useListDashboardsQuery({
    variables: {
      tenantID,
      userID,
      filter: {
        useCase: DashboardUseCase.Template,
      },
      sortAsc,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) =>
      data.listDashboards && onCompleted?.(data.listDashboards.dashboards),
  });

  const [isLoadingMore, fetchMore] = useFetchMore(
    eFetchMore,
    data?.listDashboards?.nextCursor,
  );

  useEffect(() => {
    const hasMore = data ? !!data.listDashboards?.nextCursor : true;
    if (loading || isLoadingMore || !hasMore) {
      return;
    }
    fetchMore();
  }, [data, fetchMore, isLoadingMore, loading]);

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: 'Failed to get templates list',
        props: {},
      });
    }
  }, [error, showErrorNotification]);

  return {
    templates: data?.listDashboards?.dashboards,
    loading: loading || isLoadingMore,
    error,
    refetch,
  } as const;
};
